import axios, {
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import qs from "qs";
import { ResultCode } from "@/enums/common/result.enum";
import { getQBToken, removeQBToken } from "@/utils/auth/cookies";
import { ElMessage, ElMessageBox } from "element-plus";

// 防止重复弹窗的标志
let isShowingExpiredDialog = false;

/**
 * 清理失效令牌并引导用户返回当前项目工作台重新进入跟读
 * @param message 弹窗中展示的失效原因
 */
function handleExpiredQuestionBankToken(message: string): void {
  removeQBToken();
  if (isShowingExpiredDialog) return;

  isShowingExpiredDialog = true;
  ElMessageBox.confirm(message, "提示", {
    confirmButtonText: "返回工作台",
    cancelButtonText: "取消",
    type: "warning",
  })
    .then(() => {
      // 当前项目使用 Hash 路由，直接切换 hash 可避免遗留项目地址污染
      window.location.hash = "/office/workbench";
    })
    .catch(() => {
      // 用户可以留在当前页查看已有内容，但后续题库请求仍需重新进入初始化
    })
    .finally(() => {
      isShowingExpiredDialog = false;
    });
}

// 创建 axios 实例
const service = axios.create({
  baseURL: "",
  timeout: 50000,
  headers: { "Content-Type": "application/json;charset=utf-8" },
  paramsSerializer: (params) => qs.stringify(params),
});

// 请求拦截器
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // 如果是获取跟读token的请求，跳过token检查，避免循环依赖
    if (config.url?.includes("/business/content/token")) {
      // 创建取消令牌
      const cancelToken = new axios.CancelToken((cancel) => {
        window.axiosCancelTokens.push(cancel);
      });
      config.cancelToken = cancelToken;
      return config;
    }

    // 检查跟读token是否有效
    const qbToken = getQBToken();

    if (!qbToken) {
      handleExpiredQuestionBankToken(
        "题库访问令牌已失效，请返回工作台重新进入单词跟读"
      );

      // 取消请求
      return Promise.reject(new Error("跟读访问令牌已失效"));
    }

    // 创建取消令牌
    const cancelToken = new axios.CancelToken((cancel) => {
      window.axiosCancelTokens.push(cancel);
    });
    // 将取消令牌添加到请求配置中
    config.cancelToken = cancelToken;

    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    // 如果响应是二进制流，则直接返回，用于下载文件、Excel 导出等
    if (response.config.responseType === "blob") {
      return response;
    }

    const { code, msg } = response.data;

    // 处理跟读token失效的情况（在成功响应中也要处理）
    if (code === 40012) {
      handleExpiredQuestionBankToken(
        "当前题库页面已失效，请返回工作台重新进入单词跟读"
      );

      // 直接返回，不执行后面的代码
      return Promise.resolve({ code: 40012, msg: "页面已失效" });
    }

    if (code === ResultCode.SUCCESS) {
      return response.data;
    }
    ElMessage.error(msg || "请求错误！");
    return Promise.reject(new Error(msg || "Error"));
  },
  async (error: any) => {
    // 非 2xx 状态码处理 401、403、500 等
    const response = error.response;
    if (response) {
      const { code, msg } = response.data;

      // 处理跟读token失效的情况（从response.data.code获取）
      if (code === 40012) {
        handleExpiredQuestionBankToken(
          "当前题库页面已失效，请返回工作台重新进入单词跟读"
        );

        // 直接返回，不执行后面的代码
        return Promise.resolve({ code: 40012, msg: "页面已失效" });
      }

      // 其他错误
      ElMessage.error(msg || "请求错误！");
    }
    return Promise.reject(error.message);
  }
);

export default service;
